import type { Forecast } from './types';

/**
 * Whether a forecast has this id. Ids are compared as strings: a DCA decline's id arrives as
 * a number (its database id) while callers store a string.
 *
 * @param {Forecast} item - The forecast.
 * @param {string} id - The id.
 * @return {boolean} True when they match.
 */
export const hasId = (item: Forecast, id: string) => String(item.id) === id;

/**
 * Whether a list holds the forecast with this id, compared as {@link hasId} does.
 *
 * @param {Forecast[]} list - The forecasts to search.
 * @param {string} id - The id to find.
 * @return {boolean} True when the list holds it.
 */
export const holdsId = (list: Forecast[], id: string) =>
  list.some((item) => hasId(item, id));

/**
 * Text as the picker shows it: the acronyms "PDP" and "TC" (type curve) upper-cased wherever
 * they stand as a word — DCA forecasts are named after their scenario, e.g. "pdp default_…",
 * and scenarios read "legacy tc". Display only; the saved values keep their stored case.
 *
 * @param {string} text - The stored text.
 * @return {string} The text to show.
 */
export const withAcronymsUpperCased = (text: string) =>
  text.replace(/\b(?:pdp|tc)\b/gi, (acronym) => acronym.toUpperCase());

/**
 * A stored value (a source, DCA scenario or status, e.g. "legacy", "pdp default", "eval") as the
 * picker shows it: each space-separated word starts upper-case — the rest is left as given,
 * so "ARIES" and "SQLLoader" keep theirs — and acronyms read as {@link withAcronymsUpperCased}.
 *
 * @param {string} value - The stored value.
 * @return {string} The value to show, e.g. "Legacy", "PDP Default", "Legacy TC".
 */
export const displayValue = (value: string) =>
  withAcronymsUpperCased(
    value.replace(/(^|\s)\S/g, (start) => start.toUpperCase())
  );
